// supervisor-proposal-post-apply-checks.ts
//
// The checks applyProposedChangesForAttempt runs on the batch AFTER applying
// it in memory (remap + synthetic supersedes done) and BEFORE any file is
// written (N10):
//   - R-25 (D2): the DAG of the WHOLE batch — an unknown dep or a cycle refuses
//     the batch (0 files written); a missing testcase/recipe only notifies.
//   - 3.40.0 (D3.2/D3.4): a RECOVERY decision's batch may not hang a
//     dependency or orphan a RED window. Skipped on a crash-then-replay pass
//     (review G3 M2): already-receipted changes are skipped, so this pass sees
//     neither their creates nor their target→id pairs and would refuse a
//     batch the first pass accepted — the apply is idempotent per (runId, index).
import type { ProposalRejectionCode } from "./proposal-change-types.js";
import type { BatchGovernanceInput } from "./recovery-batch-governance-scope.js";
import { type RecoveryBatchPass, recoveryBatchRejection } from "./recovery-batch-proposal-rules.js";
import type { Ticket } from "./schemas/ticket-schema.js";
import { checkTicketDagForBatch } from "./ticket-file-persistence.js";

export interface PostApplyPass extends RecoveryBatchPass {
  after: Ticket[];
  /** True when this pass skipped a change already receipted (replay). */
  replayed: boolean;
}

export function postApplyBatchRejection(
  input: BatchGovernanceInput & { notify?: (line: string) => void },
  pass: PostApplyPass,
): { reason: string; code: ProposalRejectionCode } | undefined {
  const dagCheck = checkTicketDagForBatch(pass.after, input.notify);
  if (!dagCheck.ok) return { reason: dagCheck.reason, code: "dag" };
  if (pass.replayed) return undefined;
  return recoveryBatchRejection(input, pass);
}
